package es.subastia.auctiondetail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import es.subastia.domain.usecase.GetAuctionDetailUseCase
import es.subastia.domain.usecase.IsFavoriteUseCase
import es.subastia.domain.usecase.ObserveAuthStateUseCase
import es.subastia.domain.usecase.ToggleFavoriteUseCase
import es.subastia.ui.mapper.toUi
import es.subastia.ui.model.AuctionUiModel
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.error_auction_not_found
import subastia.composeapp.generated.resources.error_unknown

sealed interface AuctionDetailUiState {
    data object Loading : AuctionDetailUiState
    data class Success(val auction: AuctionUiModel, val isFavorite: Boolean = false, val isLoggedIn: Boolean = false) : AuctionDetailUiState
    data class Error(val error: StringResource) : AuctionDetailUiState
}

class AuctionDetailViewModel(
    private val getAuctionDetailUseCase: GetAuctionDetailUseCase,
    private val observeAuthStateUseCase: ObserveAuthStateUseCase,
    private val isFavoriteUseCase: IsFavoriteUseCase,
    private val toggleFavoriteUseCase: ToggleFavoriteUseCase,
    private val auctionId: String,
) : ViewModel() {

    @OptIn(ExperimentalCoroutinesApi::class)
    val uiState = combine(
        getAuctionDetailUseCase(auctionId),
        observeAuthStateUseCase().map { it?.uid }.distinctUntilChanged(),
        observeAuthStateUseCase().flatMapLatest { user ->
            if (user != null) {
                isFavoriteUseCase(user.uid, auctionId)
            } else {
                flowOf(false)
            }
        }.distinctUntilChanged(),
    ) { auction, uid, isFav ->
        if (auction != null) {
            @Suppress("USELESS_CAST")
            AuctionDetailUiState.Success(auction.toUi(), isFav, uid != null) as AuctionDetailUiState
        } else {
            @Suppress("USELESS_CAST")
            AuctionDetailUiState.Error(Res.string.error_auction_not_found) as AuctionDetailUiState
        }
    }.catch { e ->
        emit(AuctionDetailUiState.Error(Res.string.error_unknown))
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = AuctionDetailUiState.Loading,
    )

    fun toggleFavorite() {
        viewModelScope.launch {
            val uid = observeAuthStateUseCase().map { it?.uid }.first()
            if (uid != null) {
                toggleFavoriteUseCase(uid, auctionId)
            }
        }
    }
}
